import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { adminStyles as styles } from './components/adminStyles';
import { Ionicons } from '@expo/vector-icons';
import CacheMonitoringTab from './components/CacheMonitoringTab';
import SGPCacheTab from './components/SGPCacheTab';
import BestBetsCacheTab from './components/BestBetsCacheTab';
import PlayersCacheTab from './components/PlayersCacheTab';
import SiteHealthTab from './components/SiteHealthTab';
import EmailTestingTab from './components/EmailTestingTab';
import NotificationPreferencesTab from './components/NotificationPreferencesTab';
import QueryAnalyticsTab from './components/QueryAnalyticsTab';
import LeagueManagementTab from './components/LeagueManagementTab';
import SportsbookCoverageTab from './components/SportsbookCoverageTab';
import RefreshHealthTab from './components/RefreshHealthTab';
import CoverageTab from './components/CoverageTab';
import FeatureFlagsTab from './components/FeatureFlagsTab';
import OnboardingTab from './components/OnboardingAdminTab';
import UsersTab from './components/UsersTab';
import AiTab from './components/AiTab';
import MetricsTab from './components/MetricsTab';
import NotificationsTab from './components/NotificationsAdminTab';
import MaintenanceTab from './components/MaintenanceTab';
import ArchiveModeTab from './components/ArchiveModeTab';
import PreviewTab from './components/AdminPreviewTab';
import MarketAuditTab from './components/MarketAuditTab';
import EntityResolutionTab from './components/EntityResolutionTab';
import MathCoreTab from './components/MathCoreTab';
import FinancesTab from './components/FinancesTab';
import UserFeedbackTab from './components/UserFeedbackTab';
import LegalContentTab from './components/LegalContentTab';
import ResultsLabTab from './components/ResultsLabTab';
import NlpLabTab from './components/NlpLabTab';
import GameTicker from './components/GameTicker';
import { useTicker } from './context/TickerContext';
import { useNavigation } from './context/NavigationContext';
import { resolveAdminTab, adminHashFor } from './utils/adminTabRouting';

function TabButton({ label, active, onPress }) {
  return (
    <TouchableOpacity onPress={onPress} style={[styles.tabBtn, active ? styles.tabBtnActive : null]}>
      <Text style={[styles.tabBtnText, active ? styles.tabBtnTextActive : null]}>{label}</Text>
    </TouchableOpacity>
  );
}

export default function AdminScreen() {
  // Safely get navigation context
  let navigateTo = null;
  try {
    const navigation = useNavigation();
    navigateTo = navigation?.navigateTo;
  } catch (e) {
    // Navigation context not available, that's okay
    console.log('Navigation context not available');
  }
  
  // Get ticker data from context
  const { fixtures, isTickerEnabled, isTickerCollapsed, toggleCollapsed, tickerHeight } = useTicker();
  
  // Tabs organized by category - NO EMOJIS
  const tabGroups = useMemo(() => ([
    {
      title: 'Feature Flags',
      tabs: [
        { key: 'featureFlags', label: 'Feature Flags', icon: 'flag-outline', el: <FeatureFlagsTab /> },
      ]
    },
    {
      title: 'User Management',
      tabs: [
        { key: 'users', label: 'Users', icon: 'people-outline', el: <UsersTab /> },
        { key: 'metrics', label: 'Metrics', icon: 'analytics-outline', el: <MetricsTab /> },
        { key: 'onboarding', label: 'Onboarding', icon: 'rocket-outline', el: <OnboardingTab /> },
        { key: 'userFeedback', label: 'User Feedback', icon: 'chatbubble-ellipses-outline', el: <UserFeedbackTab /> },
      ]
    },
    {
      title: 'Data & Cache',
      tabs: [
        { key: 'archiveMode', label: 'Archive Mode', icon: 'archive-outline', el: <ArchiveModeTab /> },
        { key: 'leagues', label: 'Leagues', icon: 'globe-outline', el: <LeagueManagementTab /> },
        { key: 'players', label: 'Players', icon: 'person-outline', el: <PlayersCacheTab /> },
        { key: 'cache', label: 'Odds Cache', icon: 'cube-outline', el: <CacheMonitoringTab /> },
        { key: 'sgp', label: 'SGP Cache', icon: 'layers-outline', el: <SGPCacheTab /> },
        { key: 'bestBets', label: 'Best Bets Cache', icon: 'trophy-outline', el: <BestBetsCacheTab /> },
        { key: 'marketAudit', label: 'Market Audit', icon: 'bar-chart-outline', el: <MarketAuditTab /> },
      ]
    },
    {
      title: 'Labs',
      tabs: [
        { key: 'resultsLab', label: 'Results Lab', icon: 'flask-outline', el: <ResultsLabTab /> },
        { key: 'nlpLab', label: 'NLP Lab', icon: 'chatbubbles-outline', el: <NlpLabTab /> },
        { key: 'mathLab', label: 'Math Lab', icon: 'calculator-outline' },
        { key: 'coverageLab', label: 'Coverage Lab', icon: 'analytics-outline', el: <SportsbookCoverageTab /> },
      ]
    },
    {
      title: 'System',
      tabs: [
        { key: 'health', label: 'Health', icon: 'pulse-outline', el: <SiteHealthTab /> },
        { key: 'refreshHealth', label: 'Refresh Health', icon: 'reload-circle-outline', el: <RefreshHealthTab /> },
        { key: 'coverage', label: 'Coverage', icon: 'shield-checkmark-outline', el: <CoverageTab /> },
        { key: 'queryAnalytics', label: 'Query Analytics', icon: 'stats-chart-outline', el: <QueryAnalyticsTab /> },
        { key: 'maintenance', label: 'Maintenance', icon: 'construct-outline', el: <MaintenanceTab /> },
        { key: 'entityResolution', label: 'Query Resolution', icon: 'git-compare-outline', el: <EntityResolutionTab /> },
      ]
    },
    {
      title: 'Finances',
      tabs: [
        { key: 'subscriptions', label: 'Subscriptions', icon: 'card-outline', el: <FinancesTab /> },
        { key: 'preview', label: 'Preview Mode', icon: 'eye-outline', el: <PreviewTab /> },
      ]
    },
    {
      title: 'Communications',
      tabs: [
        { key: 'ai', label: 'bOPT AI', icon: 'sparkles-outline', el: <AiTab /> },
        { key: 'healthReports', label: 'Health Reports', icon: 'bar-chart-outline', el: <NotificationPreferencesTab /> },
        { key: 'notifications', label: 'Alerts', icon: 'notifications-outline', el: <NotificationsTab /> },
        { key: 'email', label: 'Email', icon: 'mail-outline', el: <EmailTestingTab /> },
      ]
    },
    {
      title: 'Legal',
      tabs: [
        { key: 'legal', label: 'TOS & Privacy', icon: 'shield-outline', el: <LegalContentTab /> },
      ]
    },
  ]), []);
  
  // Flatten for easy lookup
  const allTabs = useMemo(() => tabGroups.flatMap(g => g.tabs), [tabGroups]);
  
  const [tab, setTab] = useState('featureFlags');
  const [tabSection, setTabSection] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true); // For mobile: show/hide sidebar
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // For desktop: collapsed (icons only) vs expanded
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect screen size for mobile menu
  useEffect(() => {
    const checkScreenSize = () => {
      if (Platform.OS === 'web') {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    
    checkScreenSize();
    
    if (Platform.OS === 'web') {
      window.addEventListener('resize', checkScreenSize);
      return () => window.removeEventListener('resize', checkScreenSize);
    }
  }, []);
  
  // URL routing support - check URL hash on mount and changes.
  // Old System hashes (mathCore, mathValidation, pipelineTrace, api) alias into Labs.
  useEffect(() => {
    const handleHashChange = () => {
      const resolved = resolveAdminTab(window?.location?.hash);
      if (!resolved) return;
      if (allTabs.find(t => t.key === resolved.tab)) {
        setTab(resolved.tab);
        setTabSection(resolved.section);
      }
    };
    
    handleHashChange();
    window?.addEventListener?.('hashchange', handleHashChange);
    return () => window?.removeEventListener?.('hashchange', handleHashChange);
  }, [allTabs]);
  
  const pushAdminHash = (tabKey, section) => {
    if (window?.history?.pushState) {
      window.history.pushState(null, null, adminHashFor(tabKey, section));
    }
  };

  const handleTabChange = (tabKey) => {
    const nextSection = tabKey === 'mathLab' ? 'calculator' : null;
    setTab(tabKey);
    setTabSection(nextSection);
    if (isMobile) setSidebarOpen(false);
    pushAdminHash(tabKey, nextSection);
  };

  const handleMathSection = (section) => {
    setTabSection(section);
    pushAdminHash('mathLab', section);
  };
  
  // Back to home function
  const handleBackToHome = () => {
    if (navigateTo) {
      navigateTo('Home');
    } else {
      // Fallback: navigate via URL if navigation context not available
      if (typeof window !== 'undefined') {
        window.location.href = '/results';
      }
    }
  };

  const currentTabEl = tab === 'mathLab'
    ? <MathCoreTab initialSection={tabSection || 'calculator'} onSectionChange={handleMathSection} />
    : allTabs.find(t => t.key === tab)?.el;
  const currentTabLabel = allTabs.find(t => t.key === tab)?.label || 'Admin';

  return (
    <View style={styles.container}>
      {/* Game Ticker Only (no navbar) */}
      {isTickerEnabled && (
        <GameTicker 
          fixtures={fixtures}
          isCollapsed={isTickerCollapsed}
          onCollapseToggle={toggleCollapsed}
        />
      )}
      
      <View style={styles.mainLayout}>
        {/* Sidebar - Desktop: always visible, can collapse. Mobile: overlay */}
        {(sidebarOpen || !isMobile) && (
          <View style={[
            styles.sidebar, 
            isMobile && styles.sidebarMobile,
            !isMobile && sidebarCollapsed && styles.sidebarCollapsed
          ]}>
            {/* Sidebar Header */}
            <View style={[styles.sidebarHeader, sidebarCollapsed && !isMobile && styles.sidebarHeaderCollapsed]}>
              {(!sidebarCollapsed || isMobile) && <Text style={styles.sidebarTitle}>Admin</Text>}
              {isMobile ? (
                <TouchableOpacity onPress={() => setSidebarOpen(false)} style={styles.closeSidebarBtn}>
                  <Ionicons name="close" size={24} color="rgba(255,255,255,0.7)" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity 
                  onPress={() => setSidebarCollapsed(!sidebarCollapsed)} 
                  style={styles.collapseBtn}
                >
                  <Ionicons 
                    name={sidebarCollapsed ? 'chevron-forward' : 'chevron-back'} 
                    size={18} 
                    color="rgba(255,255,255,0.5)" 
                  />
                </TouchableOpacity>
              )}
            </View>
            
            {/* Sidebar Navigation */}
            <ScrollView style={styles.sidebarNav} showsVerticalScrollIndicator={false}>
              {tabGroups.map((group, groupIdx) => (
                <View key={group.title} style={[
                  styles.navGroup,
                  groupIdx > 0 && styles.navGroupBorder,
                  group.title === 'Labs' && styles.navGroupLabs,
                ]}>
                  {(!sidebarCollapsed || isMobile) && (
                    <Text style={[styles.navGroupTitle, group.title === 'Labs' && styles.navGroupTitleLabs]}>
                      {group.title}
                    </Text>
                  )}
                  {group.tabs.map(t => (
                    <TouchableOpacity
                      key={t.key}
                      style={[
                        styles.navItem, 
                        tab === t.key && styles.navItemActive,
                        sidebarCollapsed && !isMobile && styles.navItemCollapsed
                      ]}
                      onPress={() => handleTabChange(t.key)}
                    >
                      <Ionicons 
                        name={tab === t.key ? t.icon.replace('-outline', '') : t.icon} 
                        size={sidebarCollapsed && !isMobile ? 20 : 18} 
                        color={tab === t.key ? '#a78bfa' : 'rgba(255,255,255,0.5)'} 
                      />
                      {(!sidebarCollapsed || isMobile) && (
                        <Text style={[styles.navItemText, tab === t.key && styles.navItemTextActive]}>
                          {t.label}
                        </Text>
                      )}
                    </TouchableOpacity>
                  ))}
                </View>
              ))}
              
              {/* Back to Home at bottom */}
              <View style={[styles.sidebarFooter, sidebarCollapsed && !isMobile && styles.sidebarFooterCollapsed]}>
                <TouchableOpacity 
                  style={[styles.backHomeBtn, sidebarCollapsed && !isMobile && styles.backHomeBtnCollapsed]} 
                  onPress={handleBackToHome}
                >
                  <Ionicons name="arrow-back" size={18} color="rgba(255,255,255,0.7)" />
                  {(!sidebarCollapsed || isMobile) && (
                    <Text style={styles.backHomeBtnText}>Back to Home</Text>
                  )}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        )}
        
        {/* Main Content Area */}
        <View style={styles.contentArea}>
          {/* Top Bar (mobile only) */}
          {isMobile && !sidebarOpen && (
            <View style={styles.mobileTopBar}>
              <TouchableOpacity onPress={() => setSidebarOpen(true)} style={styles.menuBtn}>
                <Ionicons name="menu" size={24} color="#fff" />
              </TouchableOpacity>
              <Text style={styles.mobileTitle}>{currentTabLabel}</Text>
              <View style={{ width: 40 }} />
            </View>
          )}
          
          {/* Tab Content */}
          <View style={styles.body}>
            {currentTabEl}
          </View>
        </View>
        
        {/* Mobile overlay when sidebar is open */}
        {isMobile && sidebarOpen && (
          <TouchableOpacity 
            style={styles.sidebarOverlay} 
            activeOpacity={1} 
            onPress={() => setSidebarOpen(false)} 
          />
        )}
      </View>
    </View>
  );
}



